import React, { useEffect, useRef } from 'react';
import { matchPath } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import './PurchasePricingBar.css';

const PURCHASE_FLOW_PATTERNS: { path: string; end?: boolean }[] = [
  { path: '/schools', end: true },
  { path: '/school-days', end: true },
  { path: '/school/:schoolName/day/:date', end: true },
  { path: '/school/:schoolName', end: true },
];

const PURCHASE_BAR_HEIGHT_VAR = '--purchase-pricing-bar-height';

export function matchesPurchaseFlow(pathname: string): boolean {
  return PURCHASE_FLOW_PATTERNS.some((pattern) => matchPath(pattern, pathname) != null);
}

function getBarVariant(): 'dark' | 'brand' {
  const v = (process.env.REACT_APP_PURCHASE_PRICING_BAR_VARIANT || 'brand').toLowerCase();
  return v === 'dark' ? 'dark' : 'brand';
}

const PurchasePricingBar: React.FC = () => {
  const { t } = useLanguage();
  const { individual, packFirst, packAdditional } = usePricing();
  const variant = getBarVariant();
  const { blocks, disclaimer, ariaLabel } = t.purchasePricingBar;
  const blockPrices = [individual, packFirst, packAdditional, packAdditional];
  const barRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const bar = barRef.current;
    if (!bar) return;

    const syncHeight = () => {
      const height = bar.getBoundingClientRect().height;
      root.style.setProperty(PURCHASE_BAR_HEIGHT_VAR, `${height}px`);
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(bar);

    return () => {
      observer.disconnect();
      root.style.removeProperty(PURCHASE_BAR_HEIGHT_VAR);
    };
  }, [disclaimer]);

  return (
    <aside
      ref={barRef}
      className={`purchase-pricing-bar purchase-pricing-bar--${variant}`}
      role="region"
      aria-label={ariaLabel}
    >
      <div className="purchase-pricing-bar__inner">
        <div className="purchase-pricing-bar__grid">
          {blocks.map((block, index) => (
            <div key={index} className="purchase-pricing-bar__cell">
              <div
                className="purchase-pricing-bar__price-row"
                aria-label={`${blockPrices[index]} euros`}
              >
                <span className="purchase-pricing-bar__amount">{blockPrices[index]}</span>
                <span className="purchase-pricing-bar__currency" aria-hidden="true">
                  €
                </span>
              </div>
              <div className="purchase-pricing-bar__rule" aria-hidden="true" />
              <p className="purchase-pricing-bar__desc">{block.description}</p>
              <span
                className={`purchase-pricing-bar__badge ${
                  index >= 2
                    ? 'purchase-pricing-bar__badge--accent'
                    : 'purchase-pricing-bar__badge--muted'
                }`}
              >
                {block.badge}
              </span>
            </div>
          ))}
        </div>
        <p className="purchase-pricing-bar__disclaimer">{disclaimer}</p>
      </div>
    </aside>
  );
};

export default PurchasePricingBar;
